import { NavLink } from 'react-router-dom';
import { Users, Calendar, UserCheck, Package, PackageCheck, Lock, Building2, Cpu, FolderOpen, Briefcase, LogOut, MessageCircle, Settings, UserCog, UploadCloud, Moon, Sun, LayoutDashboard, Camera } from 'lucide-react';
import { hasPermission, MODULE_LABELS } from '../utils/permissions';
import { History, DatabaseBackup } from 'lucide-react';

const GateIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M3 21V5h2v16M19 21V5h2v16M5 9h14M5 19h14M8 9v10M12 9v12M16 9v10" />
  </svg>
);

const Sidebar = ({ isOpen, isDesktopHidden, onNavigate, onLogout, auth, theme, onThemeChange }) => {
  const menuItems = [
    { to: '/inicio', icon: <LayoutDashboard size={20} />, label: 'Visão geral', modulo: 'inicio' },
    { to: '/entrega', icon: <Package size={20} />, label: 'Entregas', modulo: 'entrega' },
    { to: '/delivery', icon: <PackageCheck size={20} />, label: 'Delivery', modulo: 'delivery' },
    { to: '/morador', icon: <Users size={20} />, label: 'Moradores', modulo: 'morador' },
    { to: '/visitante', icon: <UserCheck size={20} />, label: 'Visitantes', modulo: 'visitante' },
    { to: '/airbnb', icon: <Building2 size={20} />, label: 'Airbnb', modulo: 'airbnb' },
    { to: '/contratado', icon: <Briefcase size={20} />, label: 'Prestadores', modulo: 'contratado' },
    { to: '/agenda', icon: <Calendar size={20} />, label: 'Reservas', modulo: 'agenda' },
    { to: '/controle-acesso', icon: <GateIcon size={20} />, label: 'Entrada e Saída', modulo: 'controle_acesso' },
    { to: '/controle-acesso/cameras', icon: <Camera size={20} />, label: 'Controle de Acesso', modulo: 'cameras' },
    { to: '/abertura', icon: <Lock size={20} />, label: 'Abertura Remota', modulo: 'abertura' },
    { to: '/cadastro-em-massa', icon: <UploadCloud size={20} />, label: 'Cadastro em Massa', modulo: 'cadastro_massa' },
    { to: '/whatsapp', icon: <MessageCircle size={20} />, label: 'WhatsApp', modulo: 'whatsapp' },
    { to: '/dispositivos', icon: <Cpu size={20} />, label: 'Dispositivos', modulo: 'dispositivos' },
    { to: '/grupos', icon: <FolderOpen size={20} />, label: 'Grupos', modulo: 'grupos' },
    { to: '/usuarios-sistema', icon: <UserCog size={20} />, label: 'Usuários do Sistema', modulo: 'usuarios' },
    { to: '/configuracoes', icon: <Settings size={20} />, label: 'Configurações', modulo: 'configuracoes' },
    { to: '/importar', icon: <UploadCloud size={20} />, label: 'Importar', modulo: 'backup' },
    { to: '/backup', icon: <DatabaseBackup size={20} />, label: 'Backup', superAdmin: true },
    // developer-only:start
    { to: '/developer', icon: <Cpu size={20} />, label: 'Developer', superAdmin: true },
    // developer-only:end
    { to: '/logs', icon: <History size={20} />, label: 'Logs', modulo: 'logs' },
  ].map((item) => ({ ...item, label: MODULE_LABELS[item.modulo] || item.label }));

  return (
    <aside
      className={`app-sidebar fixed left-0 top-0 z-40 flex h-[100dvh] w-72 max-w-[82vw] flex-col overflow-hidden text-white shadow-xl transition-transform duration-200 lg:z-30 lg:w-64 lg:max-w-none ${
        isOpen ? 'translate-x-0' : '-translate-x-full'
      } ${isDesktopHidden ? 'lg:-translate-x-full' : 'lg:translate-x-0'}`}
    >
      <div className="flex items-center justify-start gap-3 px-5 pb-5 pt-6">
        <div className="sidebar-brand-icon flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl">
          <Building2 size={24} strokeWidth={1.5} />
        </div>
        <span className="flex flex-col items-start text-lg font-semibold tracking-tight leading-tight">
          <span>Control</span>
          <span>Condomínio</span>
        </span>
      </div>

      <p className="sidebar-caption px-6 pb-3 text-[10px] font-medium uppercase tracking-[0.2em]">Gestão e controle de acesso</p>

      <nav aria-label="Menu principal" className="min-h-0 flex-1 overflow-y-auto px-3">
        {menuItems.filter((item) => (!item.superAdmin || auth?.user?.perfil === 'super_admin') && (!item.modulo || hasPermission(auth, item.modulo, 'visualizar'))).map((item) => (
          <NavLink
            end
            key={item.to}
            to={item.to}
            aria-label={item.label}
            title={item.label}
            onClick={onNavigate}
            className={({ isActive }) =>
              `sidebar-nav-link mb-1 flex w-full items-center justify-start gap-3 rounded-xl px-3 py-3 text-sm transition-colors ${
                isActive
                  ? 'sidebar-nav-link-active font-semibold'
                  : ''
              }`
            }
          >
            {item.icon}
            <span>{item.label}</span>
          </NavLink>
        ))}
      </nav>

      <div className="sidebar-footer shrink-0 border-t p-4 pb-[calc(1rem+env(safe-area-inset-bottom))]">
        <div role="group" aria-label="Tema da interface" className="sidebar-theme-switch mb-3 grid grid-cols-2 gap-1 rounded-xl p-1">
          {[{ value: 'light', label: 'Diurno', Icon: Sun }, { value: 'dark', label: 'Noturno', Icon: Moon }].map(({ value, label, Icon }) => (
            <button key={value} type="button" onClick={() => onThemeChange(value)} aria-pressed={theme === value} className={`sidebar-theme-button flex items-center justify-center gap-2 rounded-lg px-2 py-2 text-xs font-medium transition ${theme === value ? 'sidebar-theme-button-active' : ''}`}>
              <Icon size={15} />{label}
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={onLogout}
          aria-label="Sair do sistema"
          title="Sair do sistema"
          className="sidebar-nav-link mb-3 flex w-full items-center justify-start gap-3 rounded-xl p-3 text-sm transition"
        >
          <LogOut size={20} />
          <span>Sair</span>
        </button>

        <div className="sidebar-caption text-left text-[10px]">
          <span>v1.0.0 • Sistema de Gestão</span>
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;
